import { useEffect, useRef, useState } from "react";
import { Users, Home, Award, Star } from "lucide-react";
import { motion } from "motion/react";

const stats = [
  { icon: Users, value: 150, suffix: "+", label: "Happy Students" },
  { icon: Home, value: 24, suffix: "+", label: "Quality Rooms" },
  { icon: Award, value: 3, suffix: " Years", label: "Of Excellence" },
  { icon: Star, value: 4.9, suffix: "/5", label: "Student Rating", decimal: true },
];

function Counter({ end, duration = 2, decimal = false }: { end: number; duration?: number; decimal?: boolean }) {
  const [count, setCount] = useState(0);
  const countRef = useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!hasAnimated && countRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setHasAnimated(true);
            const increment = end / (duration * 60);
            let current = 0;
            const timer = setInterval(() => {
              current += increment;
              if (current >= end) {
                setCount(end);
                clearInterval(timer);
              } else {
                setCount(current);
              }
            }, 1000 / 60);
            return () => clearInterval(timer);
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(countRef.current);
      return () => observer.disconnect();
    }
  }, [end, duration, hasAnimated]);

  return (
    <span ref={countRef}>
      {decimal ? count.toFixed(1) : Math.floor(count).toLocaleString()}
    </span>
  );
}

export function Stats() {
  return (
    <section className="py-20 bg-[#0f1f3d] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center transition-colors duration-300 hover:border-[#d4af37]/30"
            >
              <stat.icon className="w-8 h-8 text-[#d4af37] mx-auto mb-4" aria-hidden="true" />
              <div className="mb-2">
                <span className="text-5xl text-white">
                  <Counter end={stat.value} decimal={stat.decimal} />
                  <span className="text-[#d4af37]">{stat.suffix}</span>
                </span>
              </div>
              <p className="text-white/70 text-lg">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}